import type { Message, ClaudeResponse, ContentBlock } from '../types/index.ts';

const SYSTEM_PROMPT = `You are AutoWebBuilder, an expert web developer that generates complete, self-contained single-file websites.

RULES:
- Output ONLY valid HTML. No explanation, no markdown fences, no preamble.
- The entire page must be one HTML file with <style> and <script> inline.
- Make it beautiful, modern, and production-quality. Use great typography, spacing, and color.
- Import Google Fonts if needed via @import in <style>.
- Use realistic placeholder content — real-sounding company names, headlines, copy.
- Make it fully responsive (mobile-first).
- Add subtle animations and hover effects.
- DO NOT use external images — use CSS gradients, shapes, or emoji as visual elements.
- When the user asks to change something, return the FULL updated HTML file.
- Start your response with <!DOCTYPE html> immediately.`;

export interface ClaudeResult {
  content: string;
  inputTokens: number;
  outputTokens: number;
}

export async function callClaude(
  apiKey: string,
  history: Message[]
): Promise<ClaudeResult> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 8000,
      system: SYSTEM_PROMPT,
      messages: history,
    }),
  });

  const data: ClaudeResponse = await response.json();

  if (data.error) {
    throw new Error(data.error.message);
  }

  return {
    content: data.content.map((b) => b.text ?? '').join(''),
    inputTokens: data.usage?.input_tokens ?? 0,
    outputTokens: data.usage?.output_tokens ?? 0,
  };
}

export function buildTextMessage(text: string): Message {
  return { role: 'user', content: text };
}

export function buildImageMessage(base64: string, text?: string): Message {
  const content: ContentBlock[] = [
    {
      type: 'image',
      source: { type: 'base64', media_type: 'image/png', data: base64 },
    },
    {
      type: 'text',
      text: text ?? 'Build a website based on this wireframe sketch. Interpret the layout, sections, and elements and create a polished, complete website from it.',
    },
  ];
  return { role: 'user', content };
}
